import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import abiVestingV2 from 'abi/abi-vesting-v2.json'
import { AppAdmin } from 'api/app-admin'
import { VestingTypeEnum } from 'constants/vesting'
import { useContractViem } from 'hooks/contract/use-contract-viem'
import { useMultisigOwner } from 'hooks/use-multisig-owner'
import { useVestingV2 } from 'hooks/vesting/use-vesting-v2'
import { logError } from 'utils/logger'
import { useContractManager } from 'hooks/use-contract-manager'

interface VestingContextTypeV2 {
  scVesting: string
  loading: boolean
  loadingDataContract: boolean
  contract: any
  isContractManager: boolean
  isOwner: boolean
  totalUser: number
  totalToken: number
  totalClaim?: number
  type?: VestingTypeEnum
  setScVesting: (address: string | undefined) => void
  checkContractManager: () => Promise<void>
  checkOwner: () => Promise<void>
  // Contract data (from useVestingV2)
  tokenAddress: string
  firstRelease: number | null
  cliffDuration: number | null
  totalPeriods: number | null
  timePerPeriod: number | null
  [key: string]: any
}

const VestingContextV2 = createContext<VestingContextTypeV2>({
  scVesting: '0x0000000000000000000000000000000000000000',
  loading: false,
  loadingDataContract: false,
  contract: null,
  isContractManager: false,
  isOwner: false,
  totalUser: 0,
  totalToken: 0,
  totalClaim: 0,
  setScVesting: () => {},
  checkContractManager: async () => {},
  checkOwner: async () => {},
  tokenAddress: '',
  firstRelease: null,
  cliffDuration: null,
  totalPeriods: null,
  timePerPeriod: null,
})

interface VestingContextPropsV2 {
  type: VestingTypeEnum
  children: ReactNode
}

const VestingProviderV2: React.FC<VestingContextPropsV2> = ({ type, children }) => {
  const [scVesting, setScVestingState] = useState<`0x${string}`>('' as `0x${string}`)
  const [loading, setLoading] = useState(false)
  const [totalUser, setTotalUser] = useState(0)
  const [totalToken, setTotalToken] = useState(0)
  const [totalClaim, setTotalClaim] = useState(0)

  const setScVesting = useCallback((address: string | undefined) => {
    if (address) {
      setScVestingState(address as `0x${string}`)
    }
  }, [])

  const getVestingStatistical = useCallback(async () => {
    setLoading(true)
    try {
      const res = await AppAdmin.getVestingStatistical(type)
      if (res?.data.success) {
        const data = res.data.data || {}
        if (data.contractAddress) {
          setScVestingState(data.contractAddress as `0x${string}`)
        }
        setTotalUser(data?.totalUser ?? 0)
        setTotalToken(data?.totalToken ?? 0)
        setTotalClaim(data?.totalClaim ?? 0)
      }
    } catch (error) {
      logError('Error fetching vesting statistical:', error)
    } finally {
      setLoading(false)
    }
  }, [type])

  useEffect(() => {
    getVestingStatistical()
  }, [getVestingStatistical])

  const contract = useContractViem(scVesting, abiVestingV2)

  const { loading: loadingDataContract, ...resultDataContract } = useVestingV2({
    contract,
    contractAddress: scVesting,
  })

  const { isContractManager, checkContractManager } = useContractManager({ contract })
  const { isOwner, checkOwner } = useMultisigOwner({
    contract,
  })

  const value: VestingContextTypeV2 = useMemo(
    () => ({
      ...resultDataContract,
      scVesting,
      loading,
      loadingDataContract,
      contract,
      isContractManager,
      isOwner,
      totalUser,
      totalToken,
      totalClaim,
      type,
      setScVesting,
      checkContractManager,
      checkOwner,
    }),
    [
      scVesting,
      loading,
      loadingDataContract,
      contract,
      isContractManager,
      isOwner,
      totalUser,
      totalToken,
      totalClaim,
      type,
      checkOwner,
      resultDataContract,
      setScVesting,
      checkContractManager,
    ]
  )

  return <VestingContextV2.Provider value={value}>{children}</VestingContextV2.Provider>
}

const useVestingContextV2 = (): VestingContextTypeV2 => {
  const context = useContext(VestingContextV2)
  if (context === undefined) {
    throw new Error('useVestingContextV2 must be used within VestingProviderV2')
  }
  return context
}

export { useVestingContextV2, VestingProviderV2 }
